import { Dialog, Transition } from '@headlessui/react';
import Image from 'next/future/image';
import { Fragment } from 'react';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import X from '@/public/icons/account/close.svg';
import earthIcon from '@/public/icons/collection/sustainable/earth.svg';
import sproutIcon from '@/public/icons/collection/sustainable/sprout.svg';
import refreshIcon from '@/public/icons/collection/sustainable/refresh.svg';

const data = [
  {
    icon: earthIcon,
    text: '77-90% reduction in total climate impact by buying refurbished vs new.',
  },
  {
    icon: sproutIcon,
    text: 'Up to 90% Lower Carbon Footprint, lower e-waste & reusage of raw materials.',
  },
  {
    icon: refreshIcon,
    text: 'But most importantly, you make the circular economy possible!',
  },
];

const Header = ({ hideModal }: { hideModal: () => void }) => (
  <Dialog.Title
    as="div"
    className="w-full bg-[#016137] text-base font-bold text-gray-700 xs:text-lg"
  >
    <div className="relative flex h-[150px] items-center justify-center">
      <Image
        src={getEdgeUrl(
          'images/collections/product-page/sustainable/banner.png',
        )}
        height={428}
        width={900}
        alt="Sustainable Banner"
        className="h-full w-full"
        quality={100}
      />
      <div className="absolute h-full w-full max-w-[400px] p-5">
        <div className="flex h-full w-full items-center justify-center text-center ">
          <h2 className="relative text-xl text-white">
            Your choices matter — let's make the future greener together!
          </h2>
        </div>
      </div>
    </div>
    <button
      onClick={hideModal}
      type="button"
      className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
    >
      <Image
        src={X}
        alt="Close"
        className="h-2.5 w-2.5"
        height={30}
        width={30}
      />
    </button>
  </Dialog.Title>
);
const Content = () => (
  <div className="flex h-full flex-col justify-between p-5 text-sm">
    <div className="space-y-2.5">
      {data.map((item) => (
        <div className="rounded-md border p-3">
          <div className="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-700">
            <Image src={item.icon} alt="Icon" className="h-5 w-5" />
          </div>
          <p className="mx-auto max-w-[300px] text-center">{item.text}</p>
        </div>
      ))}
    </div>

    <p className="mt-5 text-center text-gray-700/50">
      * Sources: Global E-Waste Monitor 2024, ADEME Report 2022, and Cranfield
      University's 2023 E-Waste Study. Based on average emission savings per
      typical refurbished product.
    </p>
  </div>
);

const SustainableModal = ({
  showModal,
  hideModal,
}: {
  showModal: boolean;
  hideModal: () => void;
}) => (
  <Transition.Root show={showModal} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={hideModal}>
      <Transition.Child
        as={Fragment}
        enter="ease-in-out duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-30"
        leave="ease-in-out duration-500"
        leaveFrom="opacity-30"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-700" onClick={hideModal} />
      </Transition.Child>
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full md:w-[500px]">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto relative w-full">
                <div className="relative flex h-full flex-col overflow-y-auto bg-white">
                  <Header hideModal={hideModal} />
                  <Content />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

export default SustainableModal;
